import React, { useState, useEffect, useContext } from 'react';
import { ApiContext } from '../../App';
import { toast } from 'react-toastify';
import { ChevronDown, Trash2 } from 'lucide-react';
import ConfirmationDialog from '../utils/ConfirmationDialog';

const ProcessStageDisplay = ({ individualId, onStageUpdate }) => {
  const API = useContext(ApiContext);
  const [processes, setProcesses] = useState([]);
  const [positionSteps, setPositionSteps] = useState({}); // Étapes par position
  const [loading, setLoading] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [processToDelete, setProcessToDelete] = useState(null);

  // Récupérer tous les processus pour cet individu et les étapes des positions
  useEffect(() => {
    const fetchProcesses = async () => {
      setLoading(true);
      try {
        // Récupérer les processus
        const response = await API.get(`/api/positions/individual/${individualId}/processes`);
        const processesData = response.data;
        setProcesses(processesData);

        // Récupérer les étapes de chaque position
        const stepsPromises = processesData.map(async (process) => {
          try {
            const positionResponse = await API.get(`/api/positions/${process.positionId}`);
            return {
              positionId: process.positionId,
              steps: positionResponse.data.steps.filter(step => step.active).sort((a, b) => a.order - b.order)
            };
          } catch (error) {
            console.error(`Error fetching steps for position ${process.positionId}:`, error);
            return { positionId: process.positionId, steps: [] };
          }
        });

        const stepsData = await Promise.all(stepsPromises);
        const stepsMap = {};
        stepsData.forEach(({ positionId, steps }) => {
          stepsMap[positionId] = steps;
        });
        setPositionSteps(stepsMap);

      } catch (error) {
        console.error('Error fetching processes:', error);
        toast.error('Erreur lors du chargement des processus de recrutement');
      } finally {
        setLoading(false);
      }
    };

    if (individualId) {
      fetchProcesses();
    }
  }, [individualId, API]);

  // Déplacer vers une étape spécifique
  const moveToStage = async (process, newStageId, newStageName) => {
    // Vérifier si l'étape nécessite un commentaire
    /* if (newStageName.toLowerCase().includes('ko')) {
      toast.warning('Cette étape nécessite un commentaire. Utilisez AddToProcess.');
      return;
    } */

    setLoading(true);
    try {
      await API.put(
        `/api/positions/${process.positionId}/candidates/${process.candidateId}`,
        { stageId: newStageId, comment: '' }
      );

      // Mettre à jour l'état local
      setProcesses(prevProcesses =>
        prevProcesses.map(p =>
          p.positionId === process.positionId
            ? { ...p, stage: newStageName, stageId: newStageId }
            : p
        )
      );

      setOpenDropdown(null);
      toast.success(`Déplacé vers "${newStageName}"`);

      // Notifier le parent du changement
      if (onStageUpdate) {
        onStageUpdate();
      }
    } catch (error) {
      console.error('Error updating stage:', error);
      toast.error('Erreur lors du déplacement');
    } finally {
      setLoading(false);
    }
  };

  // Ouvrir le dialogue de confirmation de suppression
  const handleRemoveClick = (process) => {
    setProcessToDelete(process);
    setShowDeleteDialog(true);
  };

  // Retirer le candidat du processus
  const confirmRemoveFromProcess = async () => {
    if (!processToDelete) return;

    setLoading(true);
    try {
      await API.delete(`/api/positions/${processToDelete.positionId}/candidates/${processToDelete.candidateId}`);

      // Retirer du state local
      setProcesses(prevProcesses =>
        prevProcesses.filter(p => p.positionId !== processToDelete.positionId)
      );

      toast.success(`Retiré du processus "${processToDelete.positionTitle}"`);

      // Notifier le parent du changement
      if (onStageUpdate) {
        onStageUpdate();
      }

      setShowDeleteDialog(false);
      setProcessToDelete(null);
    } catch (error) {
      console.error('Error removing from process:', error);
      toast.error('Erreur lors de la suppression du processus');
    } finally {
      setLoading(false);
    }
  };

  // Fermer le dropdown si on clique à l'extérieur
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (openDropdown && !event.target.closest('.process-stage-dropdown')) {
        setOpenDropdown(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [openDropdown]);


  return (
    <div className="mt-3 w-fit">
      <div className="space-y-2">
        {processes.map((process) => {
          const isDropdownOpen = openDropdown === process.positionId;
          const steps = positionSteps[process.positionId] || [];
          const currentStep = steps.find(step =>
            step._id === process.stageId || step.name === process.stage
          );

          return (
            <div
              key={process.positionId}
              className="relative bg-blue-50 border border-blue-200 rounded process-stage-dropdown"
            >
              <div className="flex items-center p-2">
                {/* Icône ChevronDown */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setOpenDropdown(isDropdownOpen ? null : process.positionId);
                  }}
                  className="p-1 hover:bg-blue-100 rounded transition-colors mr-2"
                  title="Changer d'étape"
                  disabled={loading}
                >
                  <ChevronDown
                    size={16}
                    className={`text-blue-700 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`}
                  />
                </button>

                {/* Nom du poste et étape actuelle */}
                <div className="text-sm flex-1">
                  <span className="font-medium text-blue-900">{process.positionTitle}: </span>
                  <span className="text-blue-700">{currentStep ? currentStep.name : process.stage}</span>
                </div>

                {/* Bouton de suppression */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemoveClick(process);
                  }}
                  className="p-1 hover:bg-red-100 rounded transition-colors ml-2"
                  title="Retirer du processus"
                  disabled={loading}
                >
                  <Trash2 size={14} className="text-red-600" />
                </button>
              </div>

              {/* Dropdown des étapes disponibles */}
              {isDropdownOpen && steps.length > 0 && (
                <div className="border-t border-blue-200 bg-white rounded-b overflow-hidden">
                  <div className="max-h-48 overflow-y-auto p-2 space-y-1">
                    {steps.map((step) => {
                      const isCurrentStep = step._id === process.stageId || step.name === process.stage;
                      return (
                        <button
                          key={step._id}
                          onClick={() => {
                            if (!isCurrentStep) {
                              moveToStage(process, step._id, step.name);
                            }
                          }}
                          disabled={isCurrentStep || loading}
                          className={`w-full text-left px-3 py-2 rounded text-sm transition-colors ${
                            isCurrentStep
                              ? 'bg-blue-50 text-blue-700 font-medium cursor-default'
                              : 'hover:bg-gray-50 text-gray-700'
                          }`}
                        >
                          {step.name}
                          {isCurrentStep && (
                            <span className="ml-2 text-xs text-blue-600">(actuel)</span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Dialogue de confirmation de suppression */}
      <ConfirmationDialog
        isOpen={showDeleteDialog}
        onClose={() => {
          setShowDeleteDialog(false);
          setProcessToDelete(null);
        }}
        onConfirm={confirmRemoveFromProcess}
        title="Retirer du processus de recrutement"
        message={`Êtes-vous sûr de vouloir retirer ce candidat du processus "${processToDelete?.positionTitle}" ? Cette action est irréversible.`}
        confirmText="Retirer"
        cancelText="Annuler"
        isDangerous={true}
      />
    </div>
  );
};

export default ProcessStageDisplay;